import { Dispatch, MutableRefObject, SetStateAction, useEffect, useRef, useState } from 'react'
import dialMP3 from '../assets/audio/dial.mp3'
import Centrifuge from 'centrifuge'
import { getBrowserInfo, isWebRTCSupported, visitorPassportSnapshot } from '../utils'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { accessTokenState, callDataState } from '../store/call'
import { intercomErrorState } from '../store/error'
import { useTranslation } from 'react-i18next'
import { CallStatus } from '../types'
import { usePrevious } from './index'
import { useCookies } from 'react-cookie'
import { NotificationProps } from '../components/common'

const apiUrl = process.env.REACT_APP_API_URL
const DOMAIN = process.env.REACT_APP_DOMAIN
// Debug relay is opt-in only: without an explicit env value the connectRelay
// guard must skip it (the old localhost default produced a guaranteed failed
// connection attempt and console noise on every call in every environment).
const relayUrl = process.env.REACT_APP_INTERCOM_RELAY_URL ?? ''
const signalingBaseUrl =
  process.env.REACT_APP_INTERCOM_SIGNALING_URL ??
  'https://zesec-kiion-webrtc-intercom-service.kiion.io'

type SignalSource = 'centrifugo' | 'signaling'

type CallType = {
  activeCamera: number
  audioDevices: MediaDeviceInfo[]
  callClosed: boolean
  callId: string
  camOn: number
  centrifuge?: Centrifuge
  dialSound: HTMLAudioElement
  dialSoundLoop?: ReturnType<typeof setInterval>
  iceStarted: boolean
  intercomChannel: string
  localStream?: MediaStream
  micOn: number
  relayWs?: WebSocket
  rtcConnection?: RTCPeerConnection
  signalingWs?: WebSocket
  signalingToken?: string
  signalingTokenCallId?: string
  activeSignalSource?: SignalSource
  // Set when the app (callee) sends `ready`. Offers published before that are
  // premature (setConfiguration / empty PC) and echo back on Centrifugo.
  peerReady: boolean
  offerInFlight: boolean
  videoDevices: MediaDeviceInfo[]
}

// STUN-only fallback. TURN credentials are NOT hardcoded here — they are
// fetched at call setup from the signalling server (GET /intercom/ice-servers)
// and applied via RTCPeerConnection.setConfiguration(), so the credential is
// never shipped in the bundle. If that fetch fails the call still proceeds on
// STUN (direct/reflexive) without a relay fallback.
const rtcConfig: RTCConfiguration = {
  iceCandidatePoolSize: 10,
  iceServers: [{ urls: 'stun:stun.l.google.com:19305' }, { urls: 'stun:stun.l.google.com:19302' }],
}

// The passport-share ('visitor') signal carries `dateTo` for the cookie expiry.
// It may arrive as an ISO string without a timezone (the original assumption),
// an ISO string that already has a timezone, or a numeric timestamp. Blindly
// doing `new Date(dateTo + 'Z')` produces an Invalid Date for the latter two,
// and universal-cookie throws on an invalid `expires`. Parse defensively and
// return undefined (=> session cookie) when a valid date can't be derived.
const parseVisitorExpiry = (value: unknown): Date | undefined => {
  if (value == null) return undefined
  // Numeric timestamps are ambiguous between encoders (epoch seconds/ms vs.
  // reference-date seconds), so don't guess a possibly-wrong expiry.
  if (typeof value !== 'string') return undefined
  const raw = value.trim()
  if (!raw) return undefined
  const hasTimezone = /(?:z|[+-]\d{2}:?\d{2})$/i.test(raw)
  const date = new Date(hasTimezone ? raw : `${raw}Z`)
  return Number.isNaN(date.getTime()) ? undefined : date
}

const useCall = ({
  targetFrame,
  userId,
  userFrame,
  setCallStatus,
  setTargetFrameMode,
  showNotification,
}: {
  targetFrame: MutableRefObject<HTMLVideoElement | null>
  userId?: number
  userFrame: MutableRefObject<HTMLVideoElement | null>
  setCallStatus: Dispatch<SetStateAction<CallStatus>>
  setTargetFrameMode: (value: boolean) => void
  showNotification: (value: NotificationProps) => void
}) => {
  const [status, setStatus] = useState<CallStatus>(CallStatus.INITIAL)
  const token = useRecoilValue(accessTokenState)
  const callData = useRecoilValue(callDataState)
  const setError = useSetRecoilState(intercomErrorState)
  const { t } = useTranslation()
  const [cookies, setCookies, removeCookie] = useCookies()
  const [notification, setNotification] = useState<NotificationProps | null>(null)

  const prevStatus = usePrevious(status)

  const callTimer = useRef<any>(null)

  const call = useRef<CallType>({
    activeCamera: 0,
    audioDevices: [],
    callClosed: false,
    callId: '',
    camOn: 1,
    centrifuge: undefined,
    dialSound: new Audio(dialMP3),
    dialSoundLoop: undefined,
    iceStarted: false,
    intercomChannel: '',
    localStream: undefined,
    micOn: 1,
    relayWs: undefined,
    rtcConnection: undefined,
    signalingWs: undefined,
    signalingToken: undefined,
    signalingTokenCallId: undefined,
    activeSignalSource: undefined,
    peerReady: false,
    offerInFlight: false,
    videoDevices: [],
  })

  const showPassError = (e: any, id: string) => {
    console.log(e)
    // Never hand a non-string (e.g. an Error) to the notification/render tree:
    // rendering an object as a React child throws (React #31) and white-screens
    // the app. Use the caller-provided message string, else a generic fallback.
    const text = typeof e === 'string' ? e : t('pass.notDelivered')
    setNotification({ id, text, type: 'error' })
  }

  const startDialSound = () => {
    call.current.dialSound
      .play()
      .then(() => {
        call.current.dialSoundLoop = setInterval(() => {
          call.current.dialSound.play().catch((e) => {
            console.log('loop play error', e)
          })
        }, 4000)
      })
      .catch((e) => {
        console.log('initial dial play error', e)
      })
  }

  const stopDialSound = () => {
    if (call.current.dialSoundLoop) {
      call.current.dialSound.pause()
      clearInterval(call.current.dialSoundLoop)
    }
  }

  const resolveRelayUrl = (rawUrl: string) => {
    if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
      if (rawUrl.startsWith('ws://')) {
        const upgraded = `wss://${rawUrl.slice(5)}`
        console.warn('[RELAY] Upgrading relay URL to TLS', upgraded)
        return upgraded
      }
    }
    return rawUrl
  }

  const normalizeHttpBase = (rawUrl: string) => {
    if (rawUrl.startsWith('ws://')) {
      return `http://${rawUrl.slice(5)}`
    }
    if (rawUrl.startsWith('wss://')) {
      return `https://${rawUrl.slice(6)}`
    }
    return rawUrl
  }

  const buildSignalingWsUrl = (token?: string, callId?: string) => {
    const base = normalizeHttpBase(signalingBaseUrl)
    const url = new URL(base)
    const wsScheme = url.protocol === 'https:' ? 'wss:' : 'ws:'
    url.protocol = wsScheme
    const trimmed = url.pathname.endsWith('/') ? url.pathname.slice(0, -1) : url.pathname
    let path = `${trimmed}/intercom/ws`
    if (callId) {
      path += `/${callId}`
    }
    url.pathname = path
    if (token) {
      url.searchParams.set('token', token)
    }
    return url.toString()
  }

  // Fetch the ICE server list (STUN + TURN-with-credentials) from the signalling
  // server and apply it to the peer connection, so the TURN credential is never
  // hardcoded/shipped. Token-gated: needs the same intercom token used for the
  // signalling WS. Best-effort — on any failure the call continues on the
  // STUN-only rtcConfig.
  const applyIceServers = async (token: string) => {
    if (!token || !call.current.rtcConnection) {
      return
    }
    try {
      const base = normalizeHttpBase(signalingBaseUrl)
      const url = new URL(base)
      const trimmed = url.pathname.endsWith('/') ? url.pathname.slice(0, -1) : url.pathname
      url.pathname = `${trimmed}/intercom/ice-servers`
      url.searchParams.set('token', token)
      const res = await fetch(url.toString(), { method: 'GET' })
      if (!res.ok) {
        console.log('[SIGNALING] ice-servers fetch failed; using STUN-only', {
          status: res.status,
        })
        return
      }
      const json = await res.json()
      const iceServers = json?.iceServers
      if (Array.isArray(iceServers) && iceServers.length > 0) {
        call.current.rtcConnection.setConfiguration({ ...rtcConfig, iceServers })
        console.log('[SIGNALING] ice-servers applied', { count: iceServers.length })
      }
    } catch (e) {
      console.log('[SIGNALING] ice-servers error; using STUN-only', e)
    }
  }

  const fetchSignalingToken = async (callId?: string) => {
    if (!callId) {
      console.log('[SIGNALING] token request skipped: missing callId')
      return null
    }
    const base = normalizeHttpBase(signalingBaseUrl)
    const url = new URL(base)
    const trimmed = url.pathname.endsWith('/') ? url.pathname.slice(0, -1) : url.pathname
    url.pathname = `${trimmed}/intercom/token`
    try {
      const res = await fetch(url.toString(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ platform: 'web', callId }),
      })
      if (!res.ok) {
        console.log('[SIGNALING] token request failed', {
          status: res.status,
          callId,
        })
        return null
      }
      const json = await res.json()
      if (json?.token) {
        call.current.signalingToken = json.token
        call.current.signalingTokenCallId = callId
        return json.token as string
      }
    } catch (e) {
      console.log('[SIGNALING] token request error', { callId, error: e })
    }
    return null
  }

  const sendViaCentrifugo = (payload: any) => {
    if (!call.current.centrifuge) {
      return
    }
    call.current.centrifuge.publish(call.current.intercomChannel, payload)
  }

  const sendViaSignaling = (payload: any) => {
    if (!call.current.signalingWs || call.current.signalingWs.readyState !== WebSocket.OPEN) {
      return
    }
    call.current.signalingWs.send(JSON.stringify(payload))
  }

  const publishSignal = (payload: any, source?: SignalSource) => {
    // Centrifugo delivers publishes back to the subscriber. Tag so we can
    // drop our own echo; the iOS callee also expects from:"web".
    const tagged = { ...payload, from: payload.from ?? 'web' }
    if (source === 'centrifugo') {
      sendViaCentrifugo(tagged)
      return
    }
    if (source === 'signaling') {
      sendViaSignaling(tagged)
      return
    }
    if (call.current.activeSignalSource) {
      publishSignal(tagged, call.current.activeSignalSource)
      return
    }
    sendViaCentrifugo(tagged)
    sendViaSignaling(tagged)
  }

  // Create the local offer after the app has sent `ready` and tracks are on
  // the PC. Do not rely on onnegotiationneeded: skipping that event (ICE
  // setConfiguration fires it before ready) can leave Chromium's
  // negotiation-needed flag set, so addTrack never fires it again and no
  // offer is sent — CallKit answers, passport/hangup still work, visitor rings.
  const publishLocalOffer = () => {
    const pc = call.current.rtcConnection
    if (!pc || call.current.callClosed || !call.current.peerReady) {
      return
    }
    if (call.current.offerInFlight) {
      return
    }
    if (pc.signalingState !== 'stable') {
      console.log('[SIGNALING] Skipping offer; PC not stable', pc.signalingState)
      return
    }
    call.current.offerInFlight = true
    pc.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: true })
      .then((desc) => pc.setLocalDescription(desc).then(() => desc))
      .then((desc) => {
        const payload = {
          type: 'offer',
          sdp: { type: desc.type, sdp: desc.sdp },
        }
        console.log('[SIGNALING] → PUBLISHING offer after ready:', payload)
        publishSignal(payload)
      })
      .catch((e) => {
        call.current.offerInFlight = false
        console.log('[SIGNALING] createOffer error', e)
      })
  }

  const connectRelay = (channel: string, callId: string) => {
    if (!relayUrl) {
      return
    }
    if (call.current.relayWs) {
      try {
        call.current.relayWs.close()
      } catch (e) {
        console.log('[RELAY] close error', e)
      }
    }
    try {
      const ws = new WebSocket(resolveRelayUrl(relayUrl))
      ws.onopen = () => {
        const payload = {
          op: 'subscribe',
          channel,
          callId,
          protocol: 'json',
        }
        ws.send(JSON.stringify(payload))
        console.log('[RELAY] subscribed', payload)
      }
      ws.onerror = (e) => {
        console.log('[RELAY] error', e)
      }
      ws.onclose = (evt) => {
        console.log('[RELAY] closed', {
          code: evt.code,
          reason: evt.reason,
          wasClean: evt.wasClean,
        })
      }
      call.current.relayWs = ws
    } catch (e) {
      console.log('[RELAY] connect error', e)
    }
  }

  const disconnectRelay = () => {
    if (!call.current.relayWs) {
      return
    }
    try {
      console.log('[RELAY] disconnect requested')
      call.current.relayWs.close()
    } catch (e) {
      console.log('[RELAY] close error', e)
    } finally {
      call.current.relayWs = undefined
    }
  }

  const connectSignalingWs = async (channel: string) => {
    if (call.current.signalingWs) {
      try {
        call.current.signalingWs.close()
      } catch (e) {
        console.log('[SIGNALING] close error', e)
      }
    }
    const currentCallId = call.current.callId
    const signalingToken =
      call.current.signalingTokenCallId === currentCallId ? call.current.signalingToken : undefined
    const tokenForCall = signalingToken ?? (await fetchSignalingToken(currentCallId))
    if (!tokenForCall) {
      console.log('[SIGNALING] token unavailable; skipping signaling websocket connect', {
        channel,
        callId: currentCallId,
      })
      return
    }
    // Pull TURN credentials from the server and apply them before the offer is
    // created (the offer is triggered later by the app's 'ready'), so ICE
    // gathering can use the relay. Best-effort; STUN-only fallback otherwise.
    await applyIceServers(tokenForCall)
    try {
      const ws = new WebSocket(buildSignalingWsUrl(tokenForCall, currentCallId))
      ws.onopen = () => {
        console.log('[SIGNALING] connected', {
          channel,
          callId: currentCallId,
          url: buildSignalingWsUrl(undefined, currentCallId),
        })
      }
      ws.onmessage = (evt) => {
        try {
          const data = JSON.parse(evt.data)
          handleSignalMessage(data, 'signaling')
        } catch (e) {
          console.log('[SIGNALING] parse error', e)
        }
      }
      ws.onerror = (e) => {
        console.log('[SIGNALING] error', e)
      }
      ws.onclose = (evt) => {
        console.log('[SIGNALING] closed', {
          code: evt.code,
          reason: evt.reason,
          wasClean: evt.wasClean,
        })
      }
      call.current.signalingWs = ws
    } catch (e) {
      console.log('[SIGNALING] connect error', e)
    }
  }

  const disconnectSignalingWs = () => {
    if (!call.current.signalingWs) {
      return
    }
    try {
      call.current.signalingWs.close()
    } catch (e) {
      console.log('[SIGNALING] close error', e)
    } finally {
      call.current.signalingWs = undefined
    }
  }

  const findFrontFacingCamera = (devices: MediaDeviceInfo[]): number => {
    // Try to find front-facing camera by label patterns (more comprehensive)
    const frontCameraIndex = devices.findIndex((device) => {
      const label = device.label.toLowerCase()
      return (
        label.includes('front') ||
        label.includes('user') ||
        label.includes('facing') ||
        label.includes('selfie') ||
        label.includes('self') ||
        label.includes('camera 2') || // Often front camera on dual camera devices
        label.includes('camera2') ||
        label.includes('cam 2') ||
        label.includes('cam2')
      )
    })

    if (frontCameraIndex !== -1) {
      return frontCameraIndex
    }

    // Default to first camera (index 0) - standard for iPhone, iPad, and most devices
    // The first camera is typically the front-facing camera on mobile devices
    return 0
  }

  const detectFrontCameraByTesting = async (devices: MediaDeviceInfo[]): Promise<number> => {
    // Try to detect front camera by testing each device briefly
    const accessibleCameras: number[] = []

    for (let i = 0; i < devices.length; i++) {
      try {
        const testStream = await navigator.mediaDevices.getUserMedia({
          video: { deviceId: { exact: devices[i].deviceId } },
          audio: false,
        })

        // Stop the test stream immediately
        testStream.getTracks().forEach((track) => track.stop())

        // This camera is accessible
        accessibleCameras.push(i)
      } catch (error) {
        // Camera not accessible, continue to next
      }
    }

    // If the currently selected camera is not accessible, find a better one
    if (!accessibleCameras.includes(call.current.activeCamera)) {
      // Try to find a front-facing camera among accessible ones
      for (const cameraIndex of accessibleCameras) {
        const device = devices[cameraIndex]
        const label = device.label.toLowerCase()

        // Check if this accessible camera might be front-facing
        if (
          label.includes('front') ||
          label.includes('user') ||
          label.includes('facing') ||
          label.includes('selfie') ||
          label.includes('self') ||
          label.includes('camera 2') ||
          label.includes('camera2') ||
          label.includes('cam 2') ||
          label.includes('cam2')
        ) {
          return cameraIndex
        }
      }

      // If no front camera found among accessible ones, use the last accessible camera
      // (often front camera on mobile devices)
      if (accessibleCameras.length > 0) {
        const fallbackIndex = accessibleCameras[accessibleCameras.length - 1]
        return fallbackIndex
      }
    }

    // Return current selection if it's accessible
    return call.current.activeCamera
  }

  const getVideoConstraints = () => {
    if (call.current.videoDevices.length === 1) {
      // For single camera, try both facingMode and direct device selection
      return {
        video: {
          deviceId: { exact: call.current.videoDevices[0].deviceId },
          facingMode: 'user',
        },
        audio: true,
      }
    }
    if (call.current.videoDevices.length > 1) {
      const selectedDevice = call.current.videoDevices[call.current.activeCamera]

      return {
        video: {
          deviceId: { exact: selectedDevice.deviceId },
          facingMode: 'user', // Prefer front camera
          width: {
            min: 640,
            max: 1280,
          },
        },
        audio: { deviceId: { exact: call.current.audioDevices[0].deviceId } },
      }
    }

    return {
      video: { facingMode: 'user' },
      audio: true,
    }
  }

  const stopStream = () => {
    disconnectRelay()
    try {
      stopDialSound()
      clearTimeout(callTimer.current)
      call.current.iceStarted = false
      call.current.callClosed = true
      if (call.current.localStream) {
        // @ts-expect-error exists method
        if (call.current.rtcConnection?.removeStream) {
          call.current.localStream.getTracks().forEach((t) => t.stop())
          // @ts-expect-error exists method
          call.current.rtcConnection?.removeStream(call.current.localStream)
        } else {
          call.current.localStream.getTracks().forEach((track) => {
            call.current.localStream?.removeTrack(track)
            const sender = call.current.rtcConnection
              ?.getSenders()
              ?.find((sender) => sender.track === track)
            if (sender) {
              call.current.rtcConnection?.removeTrack(sender)
            }
          })
        }
      }
      call.current.localStream = undefined
      if (userFrame.current) {
        userFrame.current.srcObject = null
      }
      if (targetFrame.current) {
        targetFrame.current.srcObject = null
      }
    } catch (e: any) {
      console.log(e)
    } finally {
      resetCall()
    }
  }

  const stopCall = () => {
    stopDialSound()
    // Tell the callee over the signalling channel that the call is over.
    // Apps listen for {type:'completed'} on the WS relay; relying only on the
    // REST hangup left them waiting for a backend push (or their own timeout).
    try {
      publishSignal({ type: 'completed', callId: call.current.callId })
    } catch (e) {
      console.log('[SIGNALING] completed publish error', e)
    }
    fetch(`${apiUrl}api/intercom/caller/hangup`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        UUID: call.current.callId,
      }),
    })
      .catch((e) => {
        console.log('cancelCallRequest error', e)
      })
      .finally(() => {
        stopStream()
      })
  }

  const handleSignalMessage = (payload: any, source: SignalSource) => {
    if (!payload) {
      console.error('[SIGNALING] Received empty payload')
      return
    }
    // Filter out messages from web client (ignore own messages)
    if (payload.from === 'web') {
      console.log('[SIGNALING] Ignoring message from web client:', payload.type)
      return
    }
    const type = payload?.type ?? payload?.Type
    if (!type) {
      console.error('[SIGNALING] Message missing type field:', payload)
      return
    }
    const normalizedType = String(type).toLowerCase()
    // Call *status* events (accepted/completed/...) are broadcast by the Zesec
    // backend over Centrifugo regardless of which transport the peer app uses
    // for WebRTC signalling. They must neither pin the active source nor be
    // dropped by the source gate: a backend 'accepted' arriving on Centrifugo
    // a beat before the app's 'ready' on the signalling WS used to pin
    // Centrifugo as active, after which the app's ready/answer/candidates on
    // the signalling WS were all discarded and no offer was ever sent.
    // Passport (`visitor`) is sent on the app's signalling WS and must also
    // pass the gate even if SDP already pinned Centrifugo.
    // `ready` must not pin: it only means the app can receive an offer. Pinning
    // it to Centrifugo (REST accept still publishes there) would send the offer
    // on Centrifugo, which the app no longer reads.
    // Only true peer SDP (offer/answer/candidate) pins the source.
    const isStatusEvent = [
      'accepted',
      'completed',
      'rejected',
      'busy',
      'timeout',
      'cancelled',
      'visitor',
      'ready',
    ].includes(normalizedType)
    if (!isStatusEvent) {
      if (call.current.activeSignalSource && call.current.activeSignalSource !== source) {
        console.log('[SIGNALING] Ignoring message from inactive source', {
          activeSource: call.current.activeSignalSource,
          ignoredSource: source,
          type,
        })
        return
      }
      // Pin future replies to the first valid remote *signalling* source.
      call.current.activeSignalSource = source
    }
    console.log(`[SIGNALING] ← Received from ${payload.from || 'unknown'}:`, {
      type,
      from: payload.from,
      source,
      isStatusEvent,
      hasSDp: !!payload.sdp,
    })
    switch (normalizedType) {
      case 'ready':
        console.log('message', 'ready')
        call.current.peerReady = true
        console.log('getVideoConstraints', JSON.stringify(getVideoConstraints()))
        navigator.mediaDevices
          .getUserMedia(getVideoConstraints())
          .then((stream) => {
            call.current.localStream = stream
            call.current.localStream.getAudioTracks()[0].enabled
            // @ts-expect-error exists method
            if (call.current.rtcConnection?.addStream) {
              // @ts-expect-error exists method
              call.current.rtcConnection?.addStream(call.current.localStream)
              console.log('centrifugeInit addStream')
            } else {
              stream.getTracks().forEach((track) => {
                const sender = call.current.rtcConnection?.addTrack(track, stream)
                console.log('centrifugeInit no addStream', sender)
              })
              console.log('centrifugeInit no addStream')
            }
            console.log('centrifugeInit rtcConnection', call.current.rtcConnection)
            console.log('centrifugeInit userFrame', userFrame.current)
            if (userFrame.current) {
              userFrame.current.srcObject = call.current.localStream
              userFrame.current.volume = 1.0
            }
            publishLocalOffer()
          })
          .catch((e: DOMException) => {
            stopDialSound()
            let errorMessage = t('error.camera')

            if (e.name === 'NotAllowedError') {
              const browserInfo = getBrowserInfo()
              if (browserInfo.isIOSChrome) {
                errorMessage = t('error.cameraPermissionIosChrome')
              } else {
                errorMessage = t('error.cameraPermission')
              }
            } else if (e.name === 'NotReadableError') {
              errorMessage = t('error.cameraInUse')
            }

            setError(errorMessage)
            console.log('getUserMedia error in centrifugeInit', e.name, e.message)
          })
        break

      case 'offer':
        console.log('message', 'offer')
        if (!payload.sdp || !payload.sdp.type || !payload.sdp.sdp) {
          console.error('[SIGNALING] Invalid offer format - missing sdp data:', payload)
          return
        }
        {
          const pc = call.current.rtcConnection
          const state = pc?.signalingState
          if (state && state !== 'stable') {
            console.log('[SIGNALING] Ignoring offer; PC not stable', state)
            return
          }
          if (pc?.localDescription?.type === 'offer') {
            console.log('[SIGNALING] Ignoring offer; we are the caller')
            return
          }
        }
        call.current.rtcConnection
          ?.setRemoteDescription(new RTCSessionDescription(payload.sdp))
          .then(() => {
            call.current.rtcConnection
              ?.createAnswer()
              .then((answer) => {
                call.current.rtcConnection
                  ?.setLocalDescription(answer)
                  .then(() => {
                    const payload = {
                      type: 'answer',
                      sdp: answer,
                    }
                    console.log('[CENTRIFUGO] → PUBLISHING answer:', payload)
                    publishSignal(payload, source)
                  })
                  .catch((e) => {
                    console.log('answer create ld set error', e)
                  })
              })
              .catch((e) => {
                console.log('answer create error', e)
              })
          })
          .catch((e) => {
            console.log('answer send error', e)
          })
        break

      case 'pre_answer':
        console.log('pre_answer', 'ready')
        if (!payload.sdp || !payload.sdp.type || !payload.sdp.sdp) {
          console.error('[SIGNALING] Invalid pre_answer format - missing sdp data:', payload)
          return
        }
        call.current.rtcConnection
          ?.setRemoteDescription(new RTCSessionDescription(payload.sdp))
          .then(() => {
            call.current.iceStarted = true
          })
          .catch((e) => {
            console.log('answer got error', e)
          })
        break

      case 'accept_answer':
        console.log('message', 'accept_answer')
        stopDialSound()
        break

      case 'accepted':
        console.log('message', 'accepted')
        break

      case 'answer':
        console.log('message', 'answer')
        if (!payload.sdp || !payload.sdp.type || !payload.sdp.sdp) {
          console.error('[SIGNALING] Invalid answer format - missing sdp data:', payload)
          return
        }
        {
          const state = call.current.rtcConnection?.signalingState
          if (state === 'stable' || state === 'have-remote-offer') {
            console.log('[SIGNALING] Ignoring answer; wrong PC state', state)
            return
          }
        }
        stopDialSound()
        setTargetFrameMode(true)
        call.current.rtcConnection
          ?.setRemoteDescription(new RTCSessionDescription(payload.sdp))
          .then(() => {
            call.current.iceStarted = true
            callTimer.current = setTimeout(() => {
              stopCall()
            }, 60000)
          })
          .catch((e) => {
            console.log('answer got error', e)
          })
        break

      case 'rejected':
        console.log('message', 'rejected')
        if (!call.current.iceStarted) {
          stopCall()
          setStatus(CallStatus.NO_ANSWER)
        } else {
          stopDialSound()
          stopStream()
          setError('rejected')
        }
        break

      case 'busy':
        console.log('message', 'busy')
        stopDialSound()
        setStatus(CallStatus.BUSY)
        break

      case 'completed':
        console.log('message', 'completed')
        stopStream()
        setStatus(CallStatus.COMPLETED)
        break

      case 'timeout':
        console.log('message', 'timeout')
        stopDialSound()
        setStatus(CallStatus.NO_ANSWER)
        break

      case 'visitor': {
        const {
          visitor: { uuid, dateTo },
        } = payload
        setTimeout(() => {
          fetch(`${apiUrl}api/visitor/public/${uuid}`, {
            method: 'GET',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
            },
          })
            .then((res) => res.json())
            .then((json) => {
              if (json.error) {
                showPassError(t('pass.notDelivered'), uuid)
                return
              }
              const snapshot = visitorPassportSnapshot(json.Passport)
              const visitor = `${new Date().getTime()}__visitor__${uuid}`
              if (cookies[visitor]) {
                removeCookie(visitor)
                localStorage.removeItem(visitor)
              }
              const expires = parseVisitorExpiry(dateTo)
              const options: { domain?: string; expires?: Date; path: string } = {
                domain: DOMAIN,
                path: '/',
              }
              if (expires) options.expires = expires
              setCookies(visitor, uuid, options)
              localStorage.setItem(visitor, JSON.stringify(snapshot))
              setNotification({ id: uuid, text: t('pass.received'), type: 'info' })
            })
            .catch((e) => {
              showPassError(e, uuid)
            })
        }, 500)
        break
      }

      case 'candidate':
        console.log('message', 'candidate')
        if (!payload.sdp || !payload.sdp.candidate) {
          console.error('[SIGNALING] Invalid candidate format - missing sdp.candidate:', payload)
          return
        }
        call.current.rtcConnection?.addIceCandidate(new RTCIceCandidate(payload.sdp)).catch((e) => {
          console.error('[SIGNALING] Failed to add ICE candidate:', e, 'payload:', payload)
        })
        break

      default:
        console.log('message', 'UNKNOWN', payload)
    }
  }

  const centrifugeInit = (channel: string) => {
    console.log('[CENTRIFUGO] Subscribing to channel:', channel)

    call.current.centrifuge?.subscribe(channel, (message) => {
      console.log('[CENTRIFUGO] ← RECEIVED MESSAGE:', {
        channel,
        type: message.data?.type ?? message.data?.Type,
        data: message.data,
        info: message.info,
        fullMessage: message,
      })
      handleSignalMessage(message.data, 'centrifugo')
    })
  }

  const connect = async () => {
    const stream = await navigator.mediaDevices
      .getUserMedia({
        audio: { echoCancellation: false },
        video: { facingMode: 'user' },
      })
      .then((stream) => stream)
      .catch((e: DOMException) => {
        stopDialSound()
        let errorMessage = t('error.camera')

        if (e.name === 'NotAllowedError') {
          const browserInfo = getBrowserInfo()
          if (browserInfo.isIOSChrome) {
            errorMessage = t('error.cameraPermissionIosChrome')
          } else {
            errorMessage = t('error.cameraPermission')
          }
        } else if (e.name === 'NotReadableError') {
          errorMessage = t('error.cameraInUse')
        }

        setError(errorMessage)
        console.log('getUserMedia error', e.name, e.message)
      })
    await navigator.mediaDevices
      .enumerateDevices()
      .then(async (devices) => {
        call.current.videoDevices = devices.filter((device) => device.kind === 'videoinput')
        call.current.audioDevices = devices.filter((device) => device.kind === 'audioinput')
        // Set active camera to front-facing by default
        if (call.current.videoDevices.length > 0) {
          call.current.activeCamera = findFrontFacingCamera(call.current.videoDevices)

          // For devices with multiple cameras, try to detect front camera by testing
          if (call.current.videoDevices.length > 1) {
            const betterCameraIndex = await detectFrontCameraByTesting(call.current.videoDevices)
            if (betterCameraIndex !== call.current.activeCamera) {
              call.current.activeCamera = betterCameraIndex
            }
          }
        }
        if (call.current.centrifuge && token && call.current.videoDevices.length >= 1) {
          console.log('[CENTRIFUGO] Setting token and connecting...')
          call.current.centrifuge.setToken(token)
          call.current.centrifuge.connect()
        }
        stream?.getTracks().forEach((t) => t.stop())
      })
      .catch((e) => {
        stopDialSound()
        setError(e)
        console.log('centrifuge connect error', e)
      })
  }

  const startCall = async () => {
    if (!call.current.rtcConnection || !call.current.centrifuge) {
      initCall()
    }
    call.current.activeSignalSource = undefined
    call.current.peerReady = false
    call.current.offerInFlight = false
    if (isWebRTCSupported()) {
      await connect()
    } else {
      stopDialSound()
      setError(t('error.video'))
      return
    }
    console.log('start', call.current.rtcConnection)
    if (
      !call.current.rtcConnection ||
      !call.current.centrifuge ||
      !targetFrame.current ||
      !userFrame.current ||
      !userId
    ) {
      return
    }
    fetch(`${apiUrl}api/intercom/offer`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        deviceId: callData?.deviceId,
        recipientId: userId,
      }),
    })
      .then((response) => response.json())
      .then((json) => {
        if ('error' in json) {
          stopDialSound()
          setError(json.error)
        }
        //prePlay()
        call.current.callId = json.UUID
        call.current.intercomChannel = json.Channel
        centrifugeInit(call.current.intercomChannel)
        connectRelay(call.current.intercomChannel, call.current.callId)
        connectSignalingWs(call.current.intercomChannel)
        startDialSound()
        setStatus(CallStatus.CALLING)
      })
      .catch(() => {
        stopDialSound()
        setStatus(CallStatus.NO_AVAILABLE)
      })
  }

  const initCall = () => {
    console.log(
      '[CENTRIFUGO] Initializing Centrifuge connection to: wss://centrifugo.zesec.com/connection/websocket'
    )
    call.current.centrifuge = new Centrifuge('wss://centrifugo.zesec.com/connection/websocket')

    call.current.rtcConnection = new RTCPeerConnection(rtcConfig)

    call.current.centrifuge.on('error', (e) => {
      console.log('[CENTRIFUGO] ERROR:', e)
    })

    call.current.centrifuge.on('connect', (context) => {
      console.log('[CENTRIFUGO] Connected:', context)
    })

    call.current.centrifuge.on('disconnect', (context) => {
      console.log('[CENTRIFUGO] Disconnected:', context)
    })

    // @ts-expect-error deprecated property, todo should be refactored
    call.current.rtcConnection.onaddstream = (evt: any) => {
      if (targetFrame.current) {
        setStatus(CallStatus.ANSWERED)
        targetFrame.current.srcObject = evt.stream
        targetFrame.current.volume = 1.0
      }
    }

    call.current.rtcConnection.ontrack = ({ track, streams: [stream] }) => {
      track.onunmute = () => {
        console.log('TRACK UNMUTED')
        setTargetFrameMode(false)
        if (stream.getVideoTracks().length > 0) {
          stopDialSound()
          // @ts-expect-error deprecated property, todo should be refactored
          call.current.rtcConnection.onaddstream(stream)
          if (targetFrame.current) {
            targetFrame.current.srcObject = stream
            targetFrame.current.volume = 1.0
          }
        } else {
          if (targetFrame.current) {
            targetFrame.current.srcObject = null
          }
        }
      }

      track.onmute = () => {
        setTargetFrameMode(true)
        console.log('TRACK MUTED')
      }
    }

    call.current.rtcConnection.onicecandidate = async (evt: any) => {
      if (evt.candidate) {
        const payload = {
          type: 'candidate',
          sdp: evt.candidate,
        }
        console.log('[CENTRIFUGO] → PUBLISHING candidate:', payload)
        publishSignal(payload)
      }
    }

    call.current.rtcConnection.onicecandidateerror = (e: any) => {
      console.log('onicecandidateerror', e)
    }

    call.current.rtcConnection.onnegotiationneeded = () => {
      if (call.current.callClosed) {
        return
      }
      if (!call.current.peerReady) {
        console.log('[SIGNALING] Skipping offer until peer ready')
        return
      }
      publishLocalOffer()
    }

    call.current.rtcConnection.oniceconnectionstatechange = () => {
      if (
        call.current.rtcConnection &&
        call.current.rtcConnection.iceConnectionState === 'failed'
      ) {
        if (call.current.rtcConnection.restartIce) {
          call.current.rtcConnection.restartIce()
        } else {
          call.current.rtcConnection
            .createOffer({ iceRestart: true })
            .then((desc) => {
              call.current.rtcConnection
                ?.setLocalDescription(desc)
                .then(() => {
                  const payload = {
                    type: 'offer',
                    sdp: desc,
                  }
                  console.log('[CENTRIFUGO] → PUBLISHING offer (ICE restart):', payload)
                  publishSignal(payload)
                })
                .catch((e) => {
                  console.log('iceRestart ld error', e)
                })
            })
            .catch((e) => {
              console.log('iceRestart createoffer error', e)
            })
        }
      }
    }
  }

  const resetCall = () => {
    // Close before dropping the references. Assigning undefined only detaches
    // our handle: the Centrifugo socket keeps running and the peer connection
    // keeps its transceivers and media tracks alive for the life of the page,
    // so every call leaked one of each. #3
    try {
      call.current.centrifuge?.disconnect()
    } catch (e) {
      console.log('[SIGNALING] centrifuge disconnect error', e)
    }
    try {
      call.current.rtcConnection?.close()
    } catch (e) {
      console.log('[SIGNALING] rtcConnection close error', e)
    }
    call.current.centrifuge = undefined
    call.current.rtcConnection = undefined
    call.current.activeSignalSource = undefined
    call.current.peerReady = false
    call.current.offerInFlight = false
    call.current.signalingToken = undefined
    call.current.signalingTokenCallId = undefined
    disconnectSignalingWs()
  }

  useEffect(() => {
    if (notification !== null) {
      showNotification(notification)
    }
  }, [notification])

  useEffect(() => {
    initCall()
    return () => {
      clearTimeout(callTimer.current)
      disconnectRelay()
      disconnectSignalingWs()
    }
  }, [])

  useEffect(() => {
    if (prevStatus === CallStatus.NO_ANSWER && status === CallStatus.COMPLETED) {
      return
    }
    setCallStatus(status)
  }, [status])

  return {
    startCall,
    stopCall,
    stopStream,
  }
}

export default useCall
